import moment from 'moment'
import noCard from '@/assets/images/no-card.svg'
import { NO_CARD_IMAGE_URL } from '@/constants'
import { cleanStatus } from '@/helpers/text'
import { validSportTypes } from '@/helpers/sportTypes'
import { useCardStore } from '@/stores/CardStore'

export const getImage = (card, mainImageField, fieldName) => {
  const noImages = !card.front_image && !card.back_image

  return card[fieldName] 
    ? `${card[fieldName]}?_t=${new Date().getTime()}` 
    : noImages && card[`match_${mainImageField}`] 
      ? `${card[`match_${mainImageField}`]}?_t=${new Date().getTime()}` 
      : (fieldName?.includes('back_image') || mainImageField?.includes('back_image')) 
        ? null 
        : NO_CARD_IMAGE_URL
}

const generateCardData = (card = {}, selectedRanges, config) => {
    let front_image = getImage(card, 'front_image', 'front_image')
    let back_image = getImage(card, 'back_image', 'back_image') 
  
    let front_image_sm = getImage(card, 'front_image', 'front_image_sm') 
    let back_image_sm = getImage(card, 'back_image',  'back_image_sm')
  
    let front_image_md = getImage(card, 'front_image', 'front_image_md')
    let back_image_md = getImage(card, 'back_image', 'back_image_md')
  
    let status = 'draft'
    if (['processing', 'ready_to_list'].includes(card.status)) {
      status = card.status
    }
  
    let matches = []
    let bestMatch = null
  
    if (card.matches) {
      let matchData = null
  
      if (typeof card.matches === 'string') {
        try {
          matchData = JSON.parse(card.matches)
        } catch (e) {
          console.error('error parsing `matches`', card, e)
        }
      } else if (typeof card.matches === 'object') {
        matchData = card.matches
      }
  
      let haveLegacyMatches = matchData.legacyMatches && matchData.legacyMatches?.length
      
      let haveVisionMatches = matchData.vision && matchData.vision?.length
  
      let haveSelectedMatches = matchData.selected && Object.keys(matchData.selected).length
  
      if (haveLegacyMatches && matchData.legacyMatches[0].frontImageUrl) {
        haveLegacyMatches = false
        haveVisionMatches = true
        matches = matchData.legacyMatches
      } else if (haveLegacyMatches) {
        matches = matchData.legacyMatches
      } else if (haveVisionMatches) {
        matches = matchData.vision 
      }
  
      // User has selected a different match, use that match
      if (card.collx_match_id && haveSelectedMatches) {
        matches = [matchData.selected]
        
        bestMatch = {
          id: matches[0].id,
          front_image: card.frontImageUrl === '' ? noCard : matches[0].frontImageUrl,
          back_image: matches[0].backImageUrl,
          title: matches[0].set?.name + ' #' + matches[0]?.number + ' ' + (matches[0]?.name || matches[0].player?.name) + (matches[0].serialNumber ? ` #/${matches[0].serialNumber}` : ''), 
          card_number: matches[0].number,
          player: matches[0]?.name || matches[0].player?.name,
          year: matches[0].year,
          set: matches[0].set?.name || matches[0].set?.subset,
          setId: card.collx_parent_set_id || matches[0].set?.id, 
          brand: matches[0].brand?.name,
          subset: matches[0].set?.subset,
          attributes: matches[0].flags?.join(', '),
          variation: matches[0].variation,
          serialNumber: matches[0].serialNumber,
        }
      }
  
      // User has not selected a match, but we have a vision match, use the first vision match
      if (card.collx_match_id && !haveSelectedMatches && haveVisionMatches) {
        matches = card.matches.vision
        
        bestMatch = {
          id: matches[0].id,
          front_image: card.frontImageUrl === '' ? noCard : card.match_front_image,
          back_image: card.backImageUrl === '' ? noCard : card.match_back_image,
          title: matches[0].set?.name + ' #' + matches[0]?.number + ' ' + (matches[0]?.name || matches[0].player?.name) + (matches[0].serialNumber ? ` #/${matches[0].serialNumber}` : ''), // XXX
          card_number: matches[0].number,
          player: matches[0].player?.name,
          year: matches[0].year,
          set: matches[0].set?.name,
          setId: card.collx_parent_set_id || matches[0].set?.id,
          brand: matches[0].brand?.name,
          subset: matches[0].set?.subset,
          attributes: matches[0].flags?.join(', '),
          variation: matches[0].variation,
          serialNumber: matches[0].serialNumber,
        }
      }
    }
  
    const generateCustomFields = (fields) => {
      if (!fields) return {}
      
      return fields.reduce((acc, item, index) => {
        acc[`custom_fields_${index}`] = item[item.value ? 'value' : 'default_value']
        return acc
      }, {})
    }
  
    let customFieldsList = []
    let customFields = {}

    if ((!card.custom_fields || !Object.keys(card.custom_fields)?.length) && config.customFields && config.customFields.length) {
      customFieldsList = config.customFields
      customFields = generateCustomFields(config.customFields || [])
    } else if ((!card.custom_fields || !card.custom_fields?.length) && (!config.customFields || !config.customFields.length)) {
      customFields = null
      customFieldsList = null
    } else if ((card.custom_fields && (card.custom_fields?.length || Object.keys(card.custom_fields)?.length)) && (!config.customFields || !config.customFields.length)) {
      customFields = generateCustomFields(typeof card.custom_fields === 'string' ? JSON.parse(card.custom_fields) : card.custom_fields || [])
      customFieldsList = card.custom_fields
    } else {
      const fields = config.customFields.reduce((acc, item) => {
        const key = cleanStatus(item.key).toLowerCase()
        const customFieldInCard = typeof card.custom_fields === 'string' 
        ? Array.isArray(JSON.parse(card.custom_fields)) ? (JSON.parse(card.custom_fields)?.find(item => cleanStatus(item.key).toLowerCase() === key)) : JSON.parse(card.custom_fields)[key]?.value
        : Array.isArray(card.custom_fields) ? card.custom_fields.find(item => cleanStatus(item.key).toLowerCase() === key) : card.custom_fields[key]

        if (customFieldInCard) {
          acc.push({ ...item, default_value: typeof card.custom_fields === 'string' || Array.isArray(card.custom_fields) ? customFieldInCard.value : card.custom_fields[key] })
        } else {
          acc.push(item)
        }
  
        return acc
      }, [])

      customFields = generateCustomFields(fields)
      customFieldsList = fields
    }
 
    let cardData = {
      id: card.id,
      viewed: card.viewed,
      is_loading: card.status === 'processing',
      has_errors: card.has_errors || (card.errors && card.errors?.length > 0),
      errors: card.errors?.notice_required || card.errors,
      has_banner: card.has_banner,
      has_suggested_edits: card.has_suggested_edits || (card.suggested_edits && card.suggested_edits?.length > 0),
      suggested_edits: card.suggested_edits || {},
      green: !card.has_errors && (card.errors && !card.errors.length > 0) && !card.has_suggested_edits && (card.suggested_edits && !card.suggested_edits.length),
      yellow: (!card.has_errors && (card.errors && !card.errors.length > 0)) && (card.has_suggested_edits || (card.suggested_edits && card.suggested_edits.length > 0)),
      red: card.has_errors || (card.errors && card.errors.length > 0),
      select: selectedRanges ? !!selectedRanges.find(item => item.id === card.id) : false,
      front_image: front_image,
      back_image: back_image,
      front_image_sm: front_image_sm,
      back_image_sm: back_image_sm,
      front_image_md: front_image_md,
      back_image_md: back_image_md,
      title: card.title,
      template_title: card.title_template,
      template_title_id: card.title_template_id,
      template_description_id: card.description_template_id,
      database_match: bestMatch || {},
      matches: matches,
      llm: card.matches?.llm,
      selected: card.matches?.selected,
      status: status,
      start_price: card.start_price,
      collx_match_id: card.collx_match_id,
      sport: card.sport ? card.sport === 'NON_SPORT' ? 'Non-Sport' : card.sport : null,
      year: card.year,
      brand: card.brand,
      player: card.player,
      card_number: card.number,
      subset: card.subset,
      attributes: card.flags,
      team: card.team,
      set: card.set,
      variation: card.variation,
      graded: card.graded,
      grader: card.grader,
      condition: card.condition,
      cert_no: card.certification_number,
      grade_name: card.grade_name,
      grade_number: card.grade_number,
      purchase_price: card.purchase_price,
      purchase_date:  card.purchase_date ? moment(card.purchase_date).format('YYYY-MM-DD') : card.purchase_date,
      note: card.note,
      inventory_location: 'FIXME',
      cabinet: card.cabinet,
      shelf: card.shelf,
      box: card.box,
      row: card.row,
      section: card.section,
      sku: card.sku,
      description: card.description,
      description_plain_text: card.description && String(card.description).replace(/<\/(li|p|div|ul|ol|h[1-6])>/gi, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/gi, '').replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim(),
      template_description_plain_text: card.description_template && String(card.description_template)?.replace(/<style[\s\S]*?<\/style>/gi, '')?.replace(/<\/(li|p|div|ul|ol|h[1-6])>/gi, '\n')?.replace(/<br\s*\/?>/gi, '\n')?.replace(/<[^>]+>/gi, '')?.replace(/[ \t]+/g, ' ')?.replace(/\n{2,}/g, '\n')?.trim(),
      template_description: card.description_template,
      type: card.type,
      customFieldsListForInspector: customFieldsList,
      frontOrientation: card.front_orientation,
      backOrientation: card.back_orientation,
      serial_number: card.serial_number,
      cert_data: card.cert_data,
      collx_listed_at: card.collx_listed_at,
      ebay_listed_at: card.ebay_listed_at,
      whatnot_listed_at: card.whatnot_listed_at,
      ebay_offer_id: card.ebay_offer_id,
      inventoryStatus: card.inventoryStatus || null,
      quantity: card.quantity,
      batch_id: card.batch_id,
      restockable: card.restockable,
      listingId: card.listingId,
      created_at: card.created_at,
      updated_at: card.updated_at,
      collxListedStatus: card.collxListedStatus || (card.listing && card.listing.collx_status),
      ebayListedStatus: card.ebayListedStatus || (card.listing && card.listing.ebay_status),
      whatnotListedStatus: card.whatnotListedStatus || (card.listing && card.listing.whatnot_status),
      shopifyListedStatus: card.shopifyListedStatus || (card.listing && card.listing.shopify_status),
      readonlyListing: card.readonlyListing,
      availableQuantity: card.quantity_available || card.quantity,
      ebayListingId: card.ebayListingId,
      whatnotListingId: card.whatnotListingId,
      collx_price: card.collx_price,
      ebay_price: card.ebay_price,
      whatnot_price: card.whatnot_price,
      shopify_price: card.shopify_price,
      card_origin: !card.card_origin || card.card_origin === 'cdp' ? null : card.card_origin.split('-')[2],
      listing: card.listing,
      has_price_recommendation: card.has_price_recommendation,
      price_recommendation: card.price_recommendation,
      weight_oz: card.weight_oz,
      ...customFields
    }
  
    return cardData  
  }

  const isValidCert = (card, selectedGrader) => {
    if (['cgc', 'sgc', 'bgs', 'bvg', 'bccg'].includes(selectedGrader)) return true

    if (['psa'].includes(selectedGrader)) {
      return !!(Number(card)) && (card.length === 8 || card.length === 9)
    }
  }

  const swapForMobile = (card) => {
    const card_ = { ...card }

    const frontImage = card_.front
    const backImage = card_.back
    const thumbnailFront = card_.thumbnailFront
    const thumbnailBack = card_.thumbnailBack

    return { ...card_, front: backImage, back: frontImage, thumbnailFront: thumbnailBack, thumbnailBack: thumbnailFront }
  }

  const getProperSport = (sport) => {
    if (validSportTypes[sport]) return validSportTypes[sport]
    return sport
  }

  const getSendLabel = (selectedCards, connectionKey, connectionName, labelType) => {
    if (!selectedCards || !Object.keys(selectedCards).length) return `List to ${connectionName}`

    const labels = {
      send: {
        send: `List to ${connectionName}`,
        sendAndUpdate: `Send/Update to ${connectionName}`,
        publish: `Update ${connectionName}`,
      },
      sending: {
        send: `Sending to ${connectionName}`,
        sendAndUpdate: `Sending/Updating to ${connectionName}`,
        publish: `Updating ${connectionName}`,
      },
      sent: {
        send: 'Listed',
        sendAndUpdate: 'Listed',
        publish: 'Listed',
      }
    }

    let alreadyListed = false
    let notListed = false

    for(const prop in selectedCards) {
      if (selectedCards[prop][`${connectionKey}ListedStatus`] === 'listed') {
        alreadyListed = true
      }

      if (selectedCards[prop][`${connectionKey}ListedStatus`] !== 'listed') {
        notListed = true
      }
    }

    if (alreadyListed && notListed) {
      return labels[labelType].sendAndUpdate
    }

    if (alreadyListed) {
      return labels[labelType].publish
    }

    if (notListed) {
      return labels[labelType].send
    }
  }

  const getUniqueCardTypes = (selectedCards) => {
    const cardsToCheck = selectedCards
    const types = []
    
    // Collect unique card types
    for (const card in cardsToCheck) {
      const cardType = cardsToCheck[card]?.type
      if (cardType && !types.includes(cardType)) {
        types.push(cardType)
      }
    }
    
    return types
  }

  const getCalculationBulkPriceFormula = {
    'Increase$': (price, fixedAmountToPrice) => Number(price) + Number(fixedAmountToPrice),
    'Increase%': (price, percentToPrice) => Number(price) * (1 + Number(percentToPrice) / 100),
    'Decrease$': (price, fixedAmountToPrice) => Number(price) - Number(fixedAmountToPrice),
    'Decrease%': (price, percentToPrice) => Number(price) * (1 - Number(percentToPrice) / 100),
  }

  const getBulkSalesValues = (options, startPrice) => {
    const { cards, selectedCalculation = null, selectedMethod = null, calculatedPriceValue = null, roundToNearestEnabled = null, roundToNearestType = null, minimalPriceEnabled = null, minimalPriceValue = null, ignoreAdjustments = null, useSuggestedPrice = null, usePurchasePrice = null } = options
    const cardStore = useCardStore()
    let startPrice_ = startPrice

    return cards.reduce((acc, card) => {
      const cardFromBulk = cardStore.cards.find(item => item.id === card.id)

      if (cardFromBulk && cardStore.anotherPriceSource) {
        startPrice_ = cardStore.anotherPriceSource === 'suggested' ? cardFromBulk.price_recommendation?.suggested_price : cardFromBulk.purchase_price
      }

      if (usePurchasePrice && card.purchase_price) {
        acc.push({
          id: card.id,
          value: card.purchase_price
        })
      } else if (useSuggestedPrice && card.has_price_recommendation && card.price_recommendation?.suggested_price) {
        const price = card.price_recommendation?.suggested_price
        acc.push({
          id: card.id,
          value: Number(price)
        })
      } else if (card.start_price && selectedCalculation) {
        if (ignoreAdjustments) {
          acc.push({
            id: card.id,
            value: startPrice_
          })
          return acc
        }


        let result = getCalculationBulkPriceFormula[`${selectedCalculation}${selectedMethod}`](startPrice_ || card.start_price, calculatedPriceValue)

        if (roundToNearestEnabled && roundToNearestType === 'Dollar') {
          result = Math.round(result)
        } else if (roundToNearestEnabled && roundToNearestType === 'Half Dollar') {
          result = Math.round(result / 0.5) * 0.5
        }

        if (minimalPriceEnabled && minimalPriceValue > result) {
          result = minimalPriceValue
        }

        acc.push({
          id: card.id,
          value: result.toFixed(2)
        })
      } else {
        acc.push({
          id: card.id,
          value: startPrice_
        })
      }

      return acc
    }, []).filter(item => item.value)
  }

  export {
    generateCardData,
    isValidCert,
    swapForMobile,
    getProperSport,
    getSendLabel,
    getUniqueCardTypes,
    getCalculationBulkPriceFormula,
    getBulkSalesValues
  }


